import { useEffect, useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { Calendar, Clock, Star, Users, ChevronRight, MapPin, Bell, MessageCircle, Wallet, Landmark, ArrowUpRight } from 'lucide-react'
import { clsx } from 'clsx'
import { useQueries, useQueryClient } from '@tanstack/react-query'
import { useProviderAuth } from '@hooks/useProviderAuth'
import { chatService } from '@lib/supabase/chat.service'
import {
  providerDashboardService,
  type ProviderAppointmentItem,
  type ProviderStats,
} from '@lib/supabase/providerDashboard.service'
import { notificationService } from '@lib/supabase/notification.service'
import { queryKeys } from '@lib/queryKeys'
import { SetupGuideFloatingButton, SetupGuidePanel, SetupGuideRail } from '@components/web/SetupGuide'
import { setupGuideService, type SetupGuideItemKey } from '@lib/supabase/setupGuide.service'

function formatCurrency(n: number) {
  return new Intl.NumberFormat('en-NG', {
    style: 'currency', currency: 'NGN', maximumFractionDigits: 0,
  }).format(n)
}
function formatDate(d: string) {
  return new Date(d).toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' })
}
function formatTime(t?: string) {
  if (!t) return ''
  const [h, m] = t.split(':')
  let hour = parseInt(h, 10)
  const ampm = hour >= 12 ? 'PM' : 'AM'
  hour = hour % 12 || 12
  return `${hour}:${m} ${ampm}`
}

const STATUS_STYLE: Record<string, string> = {
  requested: 'bg-yellow-50 text-yellow-800 border-yellow-200',
  scheduled:  'bg-emerald-50 text-emerald-800 border-emerald-200',
  completed:  'bg-blue-50 text-blue-800 border-blue-200',
  cancelled:  'bg-red-50 text-red-800 border-red-200',
}

function normalizeStatus(status?: string | null) {
  return (status || '').toLowerCase().trim()
}

function formatTime12h(t?: string | null) {
  if (!t || t === 'TBD') return 'TBD'
  const [h, m] = t.split(':').map(Number)
  const ampm = h >= 12 ? 'PM' : 'AM'
  return `${h % 12 || 12}:${String(m).padStart(2, '0')} ${ampm}`
}

function NairaIcon({ size = 18, className = '' }: { size?: number; className?: string }) {
  const sizeClass = size >= 32 ? 'text-[32px]' : size >= 20 ? 'text-[20px]' : 'text-[18px]'

  return (
    <span
      className={clsx('inline-flex items-center justify-center font-bold leading-none', sizeClass, className)}
      aria-hidden="true"
    >
      ₦
    </span>
  )
}

export default function ProviderDashboard() {
  const navigate = useNavigate()
  const { provider } = useProviderAuth()
  const queryClient = useQueryClient()
  const [isGuideOpen, setIsGuideOpen] = useState(false)

  const [statsQuery, upcomingQuery, unreadNotificationCountQuery, chatAppointmentsQuery, latestIncomingMapQuery, setupGuideQuery] = useQueries({
    queries: [
      {
        queryKey: queryKeys.provider.stats(provider?.id),
        queryFn: providerDashboardService.getStats,
        enabled: !!provider?.id,
      },
      {
        queryKey: queryKeys.provider.upcomingAppointments(provider?.id, 5),
        queryFn: () => providerDashboardService.getUpcomingAppointments(5, 'Chat Consultation'),
        enabled: !!provider?.id,
      },
      {
        queryKey: queryKeys.provider.unreadNotifications(provider?.id),
        queryFn: () => notificationService.getUnreadCountForProvider(provider!.id),
        enabled: !!provider?.id,
      },
      {
        queryKey: queryKeys.provider.chatAppointments(provider?.id),
        queryFn: () => providerDashboardService.getChatAppointments(),
        enabled: !!provider?.id,
      },
      {
        queryKey: queryKeys.provider.latestIncomingMessages(provider?.id),
        queryFn: () => chatService.getLatestIncomingPatientMessageMap(provider!.id),
        enabled: !!provider?.id,
      },
      {
        queryKey: queryKeys.provider.setupGuide(provider?.id),
        queryFn: setupGuideService.getProviderGuideStatus,
        enabled: !!provider?.id,
      },
    ],
  })

  const stats: ProviderStats | null = statsQuery.data ?? null
  const upcoming: ProviderAppointmentItem[] = upcomingQuery.data ?? []
  const chatAppointmentsRaw: ProviderAppointmentItem[] = chatAppointmentsQuery.data ?? []
  const unreadNotificationCount = unreadNotificationCountQuery.data ?? 0
  const isLoading = statsQuery.isPending || upcomingQuery.isPending
  const setupGuideStatus = setupGuideQuery.data ?? null

  const chatAppointments = useMemo(() => {
    const rank: Record<string, number> = {
      scheduled: 0,
      requested: 1,
      completed: 2,
      cancelled: 3,
      rejected: 4,
    }

    return [...chatAppointmentsRaw]
      .sort((left, right) => {
        const leftStatus = left.status?.toLowerCase() ?? ''
        const rightStatus = right.status?.toLowerCase() ?? ''
        const rankDelta = (rank[leftStatus] ?? 99) - (rank[rightStatus] ?? 99)
        if (rankDelta !== 0) return rankDelta
        return (right.createdAt || '').localeCompare(left.createdAt || '')
      })
      .filter((appointment) => !['cancelled', 'rejected'].includes(appointment.status?.toLowerCase() ?? ''))
      .slice(0, 3)
  }, [chatAppointmentsRaw])

  const unreadChatKeys = useMemo(() => {
    if (!provider?.id) return new Set<string>()
    return chatService.getUnreadConversationKeys(provider.id, chatAppointmentsRaw, latestIncomingMapQuery.data ?? {}, 'provider')
  }, [chatAppointmentsRaw, latestIncomingMapQuery.data, provider?.id])

  const hour = new Date().getHours()
  const greeting = hour < 12 ? 'Good morning' : hour < 17 ? 'Good afternoon' : 'Good evening'
  const greetingEmoji = hour < 12 ? '⛅' : hour < 17 ? '☀️' : '🌙'

  useEffect(() => {
    if (!provider?.id) return
    const subscription = notificationService.subscribeToProviderNotifications(provider.id, () => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.provider.unreadNotifications(provider.id) })
    })

    return () => {
      subscription.unsubscribe()
    }
  }, [provider?.id, queryClient])

  const STAT_CARDS = stats
    ? [
        { label: "Today's Appointments", value: String(stats.todayCount),   icon: Calendar,   color: '#04704f', bg: 'bg-primary/10' },
        { label: 'Pending Requests',      value: String(stats.pendingCount), icon: Clock,      color: '#f59e0b', bg: 'bg-yellow-50' },
        { label: 'Week Earnings',         value: formatCurrency(stats.weekEarnings), icon: Landmark, color: '#3b82f6', bg: 'bg-blue-50' },
        { label: 'Your Rating',           value: stats.ratingCount > 0 ? `${stats.rating} ★` : '—', icon: Star, color: '#f59e0b', bg: 'bg-yellow-50' },
      ]
    : []

  const handleGuideAction = (itemKey: SetupGuideItemKey) => {
    setIsGuideOpen(false)

    switch (itemKey) {
      case 'profile-photo':
        navigate('/provider/profile')
        break
      case 'service-rates':
        navigate('/provider/profile', { state: { view: 'service-rates' } })
        break
      case 'service-area':
        navigate('/provider/profile', { state: { view: 'service-areas' } })
        break
      case 'availability':
        navigate('/provider/profile', { state: { view: 'availability' } })
        break
      case 'training':
        navigate('/provider/induction?mode=revisit')
        break
      default:
        break
    }
  }

  return (
    <div className="relative p-4 pb-24 sm:p-6 sm:pb-24 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <button
            type="button"
            onClick={() => navigate('/provider/profile')}
            className="w-10 h-10 rounded-full border border-border bg-card overflow-hidden shrink-0 hover:bg-muted transition-colors"
            title="Open profile"
            aria-label="Open profile"
          >
            {provider?.profile_image_url ? (
              <img src={provider.profile_image_url} alt="Profile" className="w-full h-full object-cover" />
            ) : (
              <div className="w-full h-full flex items-center justify-center bg-primary/10 text-primary font-bold text-sm">
                {provider?.name ? provider.name.split(' ').slice(0, 2).map((part) => part[0]).join('').toUpperCase() : 'DR'}
              </div>
            )}
          </button>

          <div>
            <h1 className="text-xl font-bold text-foreground">
              {greeting} {greetingEmoji}
            </h1>
            <p className="text-base font-medium text-muted-foreground mt-0.5">{provider?.name ?? 'Healthcare Provider'}</p>
          </div>
        </div>

        <div className="flex items-center gap-2">
          <button
            title="Notifications"
            onClick={() => navigate('/provider/notifications')}
            className="relative p-2 rounded-xl border border-border hover:bg-muted transition-colors"
          >
            <Bell size={16} className="text-muted-foreground" />
            {unreadNotificationCount > 0 && (
              <span className="absolute -top-0.5 -right-0.5 min-w-4 h-4 px-1 bg-red-500 text-white text-[10px] font-bold flex items-center justify-center rounded-full">
                {unreadNotificationCount}
              </span>
            )}
          </button>
        </div>
      </div>

      {/* Stats grid */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        {isLoading
          ? Array.from({ length: 4 }).map((_, i) => (
              <div key={i} className="bg-card border border-border p-4 animate-pulse">
                <div className="w-9 h-9 rounded-xl bg-muted mb-3" />
                <div className="h-7 bg-muted rounded w-1/2 mb-2" />
                <div className="h-3 bg-muted rounded w-2/3" />
              </div>
            ))
          : STAT_CARDS.map(({ label, value, icon: Icon, color, bg }) => (
              <div key={label} className="bg-card border border-border p-4">
                <div className={clsx('w-9 h-9 rounded-xl flex items-center justify-center mb-3', bg)}>
                  <Icon size={18} style={{ color }} />
                </div>
                <p className="text-2xl font-bold text-foreground">{value}</p>
                <p className="text-xs text-muted-foreground mt-1">{label}</p>
              </div>
            ))}
      </div>

      {setupGuideStatus ? (
        <SetupGuideRail
          title="Setup Guide"
          status={setupGuideStatus}
          onOpen={() => setIsGuideOpen(true)}
          onAction={handleGuideAction}
        />
      ) : null}

      {/* Upcoming appointments */}
      <div className="bg-card border border-border overflow-hidden">
        <div className="flex items-center justify-between px-5 py-4 border-b border-border">
          <div className="flex items-center gap-2">
            <Calendar size={16} className="text-muted-foreground" />
            <h2 className="font-semibold text-foreground">Upcoming Appointments</h2>
          </div>
          <button
            onClick={() => navigate('/provider/appointments')}
            className="text-sm text-primary font-medium hover:underline flex items-center gap-1"
          >
            View all <ChevronRight size={14} />
          </button>
        </div>

        {isLoading ? (
          <div className="p-5 space-y-3">
            {[1, 2, 3].map((i) => (
              <div key={i} className="flex items-center gap-3 animate-pulse">
                <div className="w-10 h-10 rounded-full bg-muted shrink-0" />
                <div className="flex-1 space-y-1.5">
                  <div className="h-4 bg-muted rounded w-1/2" />
                  <div className="h-3 bg-muted rounded w-1/3" />
                </div>
                <div className="h-6 bg-muted rounded w-20" />
              </div>
            ))}
          </div>
        ) : upcoming.length === 0 ? (
          <div className="py-10 text-center">
            <Calendar size={32} className="text-muted-foreground/40 mx-auto mb-2" />
            <p className="text-sm text-muted-foreground">No upcoming appointments</p>
          </div>
        ) : (
          <div className="divide-y divide-border">
            {upcoming.slice(0, 3).map((appt) => (
              
              <button
                key={appt.id}
                className="w-full flex items-center gap-3 px-5 py-3.5 hover:bg-muted/50 transition-colors text-left"
                onClick={() => navigate('/provider/appointments')}
              >
                <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center shrink-0 overflow-hidden">
                  {appt.patientImage ? (
                    <img src={appt.patientImage} alt="" className="w-full h-full object-cover" />
                  ) : (
                    <Users size={18} className="text-primary" />
                  )}
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-foreground truncate">{appt.patientName}</p>
                  <p className="text-xs text-muted-foreground">
                    {appt.serviceType} · {formatDate(appt.scheduledDate)}
                    {appt.scheduledTime ? ` · ${formatTime(appt.scheduledTime)}` : ''}
                  </p>
                </div>
                <span className={clsx('text-xs px-2 py-0.5 rounded-full border font-medium shrink-0',
                  STATUS_STYLE[normalizeStatus(appt.status)] ?? 'bg-muted text-muted-foreground border-border'
                )}>
                  {appt.status}
                </span>
              </button>
            ))}
          </div>
        )}
      </div>

      <div className="border border-border bg-card p-5">
        <div className="flex items-start justify-between gap-4">
          <div>
            <h2 className="font-semibold text-foreground">Chats</h2>
            <p className="mt-1 text-sm text-muted-foreground">
              Reopen active and recent patient chat consultations.
            </p>
          </div>
          <button
            type="button"
            onClick={() => navigate('/provider/chats')}
            className="inline-flex items-center gap-1 text-sm font-medium text-primary hover:underline"
          >
            See all
            <ChevronRight size={14} />
          </button>
        </div>

        {chatAppointments.length > 0 ? (
          <div className="mt-4 space-y-3">
            {chatAppointments.map((appointment) => (
              <button
                key={appointment.id}
                type="button"
                onClick={() => navigate(`/provider/chat/${appointment.id}`, { state: { backPath: '/provider/dashboard' } })}
                className="flex w-full items-start gap-3 border border-border bg-background p-3 text-left transition-colors hover:bg-muted/40"
              >
                <div className="flex h-11 w-11 shrink-0 items-center justify-center overflow-hidden rounded-2xl bg-sky-50 text-sky-600">
                  {appointment.patientImage ? (
                    <img src={appointment.patientImage} alt="" className="h-full w-full object-cover" />
                  ) : (
                    <MessageCircle size={20} />
                  )}
                </div>

                <div className="min-w-0 flex-1">
                  <div className="flex flex-wrap items-center justify-between gap-2">
                    <div className="min-w-0">
                      <p className="truncate text-sm font-semibold text-foreground">
                        {appointment.patientName || 'Patient'}
                      </p>
                      <p className="truncate text-xs text-muted-foreground">
                        {appointment.serviceType}
                      </p>
                    </div>
                    <span className={clsx('text-xs px-2 py-0.5 rounded-full border font-medium shrink-0',
                      STATUS_STYLE[normalizeStatus(appointment.status)] ?? 'bg-muted text-muted-foreground border-border'
                    )}>
                      {appointment.status}
                    </span>
                  </div>

                  <div className="mt-2 flex flex-wrap gap-x-4 gap-y-1 text-xs text-muted-foreground">
                    <div className="flex items-center gap-1">
                      <Calendar size={12} />
                      {new Date(appointment.scheduledDate).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                    </div>
                    {appointment.scheduledTime ? (
                      <div className="flex items-center gap-1">
                        <Clock size={12} />
                        {formatTime12h(appointment.scheduledTime)}
                      </div>
                    ) : null}
                  </div>

                  <div className="mt-3 flex flex-wrap items-center gap-2">
                    {unreadChatKeys.has(chatService.getConversationKey(appointment.id, appointment.conversationId)) ? (
                      <span className="rounded-full bg-emerald-100 px-2.5 py-1 text-[11px] font-semibold uppercase tracking-[0.12em] text-emerald-700">
                        New message
                      </span>
                    ) : null}
                    <span className="inline-flex items-center gap-1.5 text-sm font-semibold text-primary">
                      Open Chat
                      <ArrowUpRight size={14} />
                    </span>
                  </div>
                </div>
              </button>
            ))}
          </div>
        ) : (
          <div className="mt-4 border border-dashed border-border p-5 text-center">
            <MessageCircle size={28} className="mx-auto text-muted-foreground/40 mb-2" />
            <p className="text-sm font-medium text-foreground">No chat consultations yet</p>
            <p className="mt-1 text-xs text-muted-foreground">Patient chat consultations will appear here for quick access.</p>
            <button
              type="button"
              onClick={() => navigate('/provider/appointments')}
              className="mt-3 text-sm font-medium text-primary hover:underline"
            >
              View appointments
            </button>
          </div>
        )}
      </div>

      {/* Quick actions */}
      <div className="grid grid-cols-2 gap-4">
        <button
          onClick={() => navigate('/provider/appointments')}
          className="bg-card border border-border p-4 text-left hover:bg-muted/50 transition-colors"
        >
          <Calendar size={20} className="text-primary mb-2" />
          <p className="font-medium text-foreground text-sm">Manage Appointments</p>
          <p className="text-xs text-muted-foreground mt-0.5">Accept, decline &amp; track</p>
        </button>
        <button
          onClick={() => navigate('/provider/profile', { state: { view: 'availability' } })}
          className="bg-card border border-border p-4 text-left hover:bg-muted/50 transition-colors"
        >
          <Clock size={20} className="text-green-600 mb-2" />
          <p className="font-medium text-foreground text-sm">Set Availability</p>
          <p className="text-xs text-muted-foreground mt-0.5">Manage your weekly schedule</p>
        </button>
        <button
          onClick={() => navigate('/provider/profile', { state: { view: 'service-areas' } })}
          className="bg-card border border-border p-4 text-left hover:bg-muted/50 transition-colors"
        >
          <MapPin size={20} className="text-blue-500 mb-2" />
          <p className="font-medium text-foreground text-sm">Service Area</p>
          <p className="text-xs text-muted-foreground mt-0.5">Cities and travel radius</p>
        </button>
        <button
          onClick={() => navigate('/provider/profile', { state: { view: 'service-rates' } })}
          className="bg-card border border-border p-4 text-left hover:bg-muted/50 transition-colors"
        >
          <NairaIcon size={20} className="text-yellow-600 mb-2" />
          <p className="font-medium text-foreground text-sm">Service Rates</p>
          <p className="text-xs text-muted-foreground mt-0.5">Set pricing for each service</p>
        </button>
        <button
          onClick={() => navigate('/provider/profile', { state: { view: 'payout' } })}
          className="col-span-2 bg-card border border-border p-4 text-left hover:bg-muted/50 transition-colors"
        >
          <Wallet size={20} className="text-purple-600 mb-2" />
          <p className="font-medium text-foreground text-sm">Payout &amp; Wallet</p>
          <p className="text-xs text-muted-foreground mt-0.5">Bank accounts, balances and payouts</p>
        </button>
      </div>

      <button
        type="button"
        onClick={() => navigate('/provider/support')}
        className="fixed bottom-4 right-4 z-20 inline-flex items-center gap-2 rounded-full bg-primary px-4 py-3 text-sm font-semibold text-primary-foreground shadow-[0_14px_30px_rgba(4,112,79,0.28)] transition-transform hover:-translate-y-0.5 hover:bg-primary/95"
      >
        <MessageCircle size={18} />
        Get Help
      </button>
      <SetupGuideFloatingButton remainingCount={setupGuideStatus?.remainingCount ?? 0} onOpen={() => setIsGuideOpen(true)} />
      <SetupGuidePanel
        open={isGuideOpen}
        title="Provider Setup"
        status={setupGuideStatus}
        onClose={() => setIsGuideOpen(false)}
        onAction={handleGuideAction}
      />
    </div>
  )
}
